import { useState, useEffect } from "react";
import { getAssignedProducts, updateProduct } from "../api/ProductsApis";
import {
  PRODUCTIVITY_OPTIONS,
  CLEANING_OPTIONS,
  MACHINE_TOOL_OPTIONS,
} from "../constants/productConfigOptions";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";

const ProductionSettings = () => {
  const { canWrite } = useAuth();
  const canEditProductSettings = canWrite("ERPagAssignedValues");
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ sku: "", title: "" });

  // Editing state – using MongoDB _id
  const [editingId, setEditingId] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [savingId, setSavingId] = useState(null);

  // Pagination
  const itemsPerPage = 25;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await getAssignedProducts();
        setProducts(response.data || []);
      } catch (err) {
        setError(err.message || "Failed to load products");
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtering
  const filteredProducts = products.filter((p) => {
    const skuMatch = p.sku
      ?.toString()
      .toLowerCase()
      .includes(filters.sku.toLowerCase());
    const titleMatch = p.description
      ?.toLowerCase()
      .includes(filters.title.toLowerCase());
    return skuMatch && titleMatch;
  });

  const totalItems = filteredProducts.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  // Editing handlers
  const startEditing = (product) => {
    if (!canEditProductSettings) {
      toast.error("You do not have permission to edit product settings.", {
        position: "top-right",
      });
      return;
    }
    setEditingId(product.sku);
    setEditedValues({
      productivity_level: product.productivity_level || "",
      cleaning_level: product.cleaning_level || "",
      machine_tool: product.machine_tool || "",
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditedValues({});
    setSavingId(null);
  };

  const handleValueChange = (field, value) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }));
  };

  const saveChanges = async (productSku) => {
    setSavingId(productSku);

    const payload = {
      ids: [productSku],
      productivity_level: editedValues.productivity_level || null,
      cleaning_level: editedValues.cleaning_level || null,
      machine_tool: editedValues.machine_tool || null,
    };

    try {
      const result = await updateProduct(payload);
      toast.success(result.message || "Updated successfully!");
      setProducts((prev) =>
        prev.map((p) => (p.sku === productSku ? { ...p, ...editedValues } : p))
      );

      cancelEditing();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Failed to update product";
      toast.error(errorMessage, { position: "top-right" });
    } finally {
      setSavingId(null);
    }
  };

  const getDisplayValue = (value) => (value ? value : "—");

  const renderPagination = () => (
    <div className="flex items-center justify-between mt-8 px-4 sm:px-6">
      <p className="text-sm text-gray-600">
        Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
        {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
        products
      </p>
      <div className="flex gap-2">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-5 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          Previous
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-5 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6 mx-auto space-y-5">
      {/* Header */}
      {!canEditProductSettings && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <strong>View-Only Access:</strong> You can view product settings but cannot make changes.
            </div>
          </div>
        </div>
      )}
      <div>
        <h3 className="text-xl font-semibold text-gray-900">
          Production Settings
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          View and update productivity, cleaning, and machine tool
          configurations
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <h2 className="text-lg font-semibold text-gray-900">
              Configured Products
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Search by SKU (e.g. 5748590001)"
            value={filters.sku}
            onChange={(e) => handleFilterChange("sku", e.target.value)}
            className="w-full px-5 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 bg-gray-50 hover:bg-white"
          />
          <input
            type="text"
            placeholder="Search by product name..."
            value={filters.title}
            onChange={(e) => handleFilterChange("title", e.target.value)}
            className="w-full px-5 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 bg-gray-50 hover:bg-white"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="text-center py-24">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading products...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-10 text-center">
          <p className="text-red-700 font-medium text-lg">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      ) : totalItems === 0 ? (
        <div className="bg-gray-50 rounded-xl p-16 text-center">
          <p className="text-xl font-semibold text-gray-700">
            No products found
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Product Name
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Productivity
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Cleaning
                    </th>
                    <th className="px-6 py-5 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Machine Tool
                    </th>
                    <th className="px-6 py-5 text-right text-xs font-bold text-gray-700 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedProducts.map((product) => {
                    const isEditing = editingId === product.sku;
                    const isSaving = savingId === product.sku;

                    return (
                      <tr
                        key={product._id}
                        className={`transition-colors ${
                          isEditing ? "bg-blue-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-6 py-4 font-mono text-sm font-semibold text-gray-900">
                          {product.sku}
                        </td>
                        <td className="px-6 py-4 text-gray-800">
                          <div
                            className="max-w-md truncate"
                            title={product.description}
                          >
                            {product.description}
                          </div>
                        </td>

                        {/* Productivity */}
                        <td className="px-6 py-4">
                          {isEditing ? (
                            <select
                              value={editedValues.productivity_level || ""}
                              onChange={(e) =>
                                handleValueChange(
                                  "productivity_level",
                                  e.target.value
                                )
                              }
                              className="px-4 py-2 text-sm rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 bg-white"
                            disabled={!canEditProductSettings}
                            >
                              {PRODUCTIVITY_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              className={`inline-flex px-full px-3 py-1.5 rounded-full text-xs font-bold tracking-wide ${
                                product.productivity_level === "Fast"
                                  ? "bg-green-100 text-green-800"
                                  : product.productivity_level === "Medium"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : product.productivity_level === "Slow"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {getDisplayValue(product.productivity_level)}
                            </span>
                          )}
                        </td>

                        {/* Cleaning */}
                        <td className="px-6 py-4">
                          {isEditing ? (
                            <select
                              value={editedValues.cleaning_level || ""}
                              onChange={(e) =>
                                handleValueChange(
                                  "cleaning_level",
                                  e.target.value
                                )
                              }
                              className="px-4 py-2 text-sm rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                              {CLEANING_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="font-medium text-gray-700">
                              {getDisplayValue(product.cleaning_level)}
                            </span>
                          )}
                        </td>

                        {/* Machine Tool */}
                        <td className="px-6 py-4">
                          {isEditing ? (
                            <select
                              value={editedValues.machine_tool || ""}
                              onChange={(e) =>
                                handleValueChange(
                                  "machine_tool",
                                  e.target.value
                                )
                              }
                              className="px-4 py-2 text-sm rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 bg-white min-w-[200px]"
                            >
                              {MACHINE_TOOL_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="font-medium text-gray-700">
                              {getDisplayValue(product.machine_tool)}
                            </span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4 text-right">
                          {isEditing ? (
                            <div className="flex justify-end gap-3">
                              <button
                                onClick={() => saveChanges(product.sku)}
                                disabled={isSaving || !canEditProductSettings}
                                className="text-green-600 hover:text-green-800 font-medium text-sm disabled:opacity-50"
                              >
                                {isSaving ? "Saving..." : "Save"}
                              </button>
                              <button
                                onClick={cancelEditing}
                                className="text-gray-500 hover:text-gray-700 font-medium text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => startEditing(product)}
                              disabled={!canEditProductSettings}
                              className={`px-3 py-2 rounded-lg text-sm font-semibold shadow transition ${
                                canEditProductSettings
                                  ? "bg-black hover:bg-gray-900 text-white"
                                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
                              }`}
                              title={
                                !canEditProductSettings
                                  ? "You do not have permission to edit settings"
                                  : "Edit product settings"
                              }
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && renderPagination()}
        </>
      )}
    </div>
  );
};

export default ProductionSettings;
